import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as React from "react";

import { cn } from "@/lib/utils";
import { FONT_SIZE } from "@/utils/constants/size";
import { AppTheme } from "@/utils/constants/theme";
import Container from "../Container";
import { ShadAlertModalProps } from "./alert-dialog";
import { Button } from "./button";

type PrimitiveProps = {
  dialogProps: React.ComponentProps<typeof DialogPrimitive.Root>;
  dialogTriggerProps: React.ComponentProps<typeof DialogPrimitive.Trigger>;
  dialogPortalProps: React.ComponentProps<typeof DialogPrimitive.Portal>;
  dialogOverlayProps: React.ComponentProps<typeof DialogPrimitive.Overlay>;
  dialogContentProps: React.ComponentProps<typeof DialogPrimitive.Content>;
  dialogHeaderProps: React.ComponentProps<"div">;
  dialogFooterProps: React.ComponentProps<"div">;
  dialogTitleProps: React.ComponentProps<typeof DialogPrimitive.Title>;
  dialogDescriptionProps: React.ComponentProps<
    typeof DialogPrimitive.Description
  >;
};

export type ModalProps = Partial<PrimitiveProps> &
  React.PropsWithChildren &
  ShadAlertModalProps & {
    maskClosable?: boolean;
    widthPercent?: number;
    accentColor?: string;
    isCurtain?: boolean;
    curtainWidth?: number;
    hasHeader?: boolean;
    maxWidth?: number;
  };

function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

// function DialogTrigger({
//   ...props
// }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
//   return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
// }

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  isCurtain = false,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay> & {
  isCurtain?: boolean;
}) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        "fixed inset-0 z-50",
        isCurtain
          ? "curtain-overlay bg-black/20"
          : "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 bg-black/50",
        className
      )}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  isCurtain = false,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  isCurtain?: boolean;
}) {
  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay isCurtain={isCurtain} />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          "overflow-hidden bg-background fixed z-50 border shadow-lg",
          isCurtain
            ? "curtain-modal top-0 right-0 h-[calc(100vh-68px)] max-h-[calc(100vh-68px)] max-w-none rounded-l-lg flex flex-col transition-all duration-300 min-w-0"
            : "data-[state=open]:animate-in data-[state=closed]:animate-out rounded-[20px] data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] grid w-full duration-200 sm:max-w-lg max-h-[90vh] overflow-y-auto",
          className
        )}
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-2 text-center sm:text-left", className)}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className
      )}
      {...props}
    />
  );
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("text-lg leading-none font-semibold", className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

const Modal = ({
  widthPercent = 50,
  maxWidth = 500,
  open,
  title,
  descriptions,
  descriptionSize = "xs",
  maskClosable = true,
  children,
  cancelButtonProps,
  continueButtonProps,
  footer,
  titleColor = AppTheme.darkJungleGreen,
  headerBackgroundColor = "#EEE",
  hasHeader = true,
  onContinue,
  onToggleChange,
  onCancel,
  accentColor,
  isCurtain = false,
  curtainWidth = 700,
  dialogContentProps,
}: ModalProps) => {
  const dSize = FONT_SIZE[descriptionSize];
  const {
    label: continueLabel,
    variant: continueVariant = "default",
    loading: continueLoading,
    disabled: continueDisabled,
  } = continueButtonProps || {};
  const {
    label: cancelLabel,
    variant: cancelVariant = "primary-outline",
    loading: cancelLoading,
    disabled: cancelDisabled,
  } = cancelButtonProps || {};

  const contentStyle = {
    width: isCurtain ? `${curtainWidth}px` : `${widthPercent}%`,
    ...dialogContentProps?.style,
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(openState) => {
        onToggleChange?.(openState);
        if (!openState && onCancel) {
          onCancel();
        }
      }}
    >
      <DialogContent
        isCurtain={isCurtain}
        {...dialogContentProps}
        style={{
          ...contentStyle,
          maxWidth: maxWidth,
          ...dialogContentProps?.style,
        }}
        className={cn(
          isCurtain ? "" : "sm:min-w-[500px] sm:max-w-[2056px]",
          dialogContentProps?.className
        )}
        {...(!maskClosable && { onInteractOutside: (e) => e.preventDefault() })}
      >
        {hasHeader && (
          <DialogHeader
            className="px-5 pt-[20px] flex flex-col justify-center"
            style={{ backgroundColor: headerBackgroundColor }}
          >
            <Container
              indention={0}
              flexDisplay
              direction="row"
              contentAlign="between"
              itemsAlign="center"
            >
              <DialogTitle
                className="flex items-center justify-start text-2xl"
                style={{ color: titleColor, fontSize: 24 }}
              >
                {title || ""}
              </DialogTitle>
            </Container>
          </DialogHeader>
        )}
        <DialogDescription
          className={`max-h-[700px] overflow-y-auto ${
            descriptions ? "px-3 py-1" : ""
          }`}
          style={{ fontSize: dSize }}
        >
          {descriptions || ""}
        </DialogDescription>
        <Container
          cornerRadius={0}
          className={
            isCurtain ? "flex-1 overflow-y-auto" : "flex-1 overflow-hidden"
          }
          indention={isCurtain ? 8 : 0}
          style={{ marginTop: descriptions ? 10 : 5 }}
        >
          {children || <></>}
        </Container>
        <DialogFooter
          className="px-6 py-4 flex-shrink-0"
          {...(footer && {
            className: cn("sm:justify-end px-6 flex-shrink-0"),
          })}
        >
          {footer || (
            <>
              <DialogClose asChild onClick={() => onCancel?.()}>
                <Button
                  variant={cancelVariant}
                  disabled={cancelDisabled}
                  loading={cancelLoading}
                  className={`min-w-[100px] px-4 border-[${accentColor}] text-[${accentColor}] text-cancel-button-text border border-cancel-button-text text-base font-medium`}
                >
                  {cancelLabel || "Cancel"}
                </Button>
              </DialogClose>
              {onContinue && (
                <Button
                  variant={continueVariant}
                  disabled={continueDisabled}
                  loading={continueLoading}
                  className="min-w-[100px] px-4 border text-base font-medium"
                  onClick={() => onContinue?.()}
                >
                  {continueLabel || "Continue"}
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default Modal;
